/**
 * 工作流页专用接口：工作流 CRUD/发布、单节点运行、DB 连接管理、URL 解析（试运行文件链）等。
 */
import { showMessage } from '@/components/antd-comp/message'
import axios from '../../../controllers/request'
import type { DatabaseTableRef } from '../nodes/database/types'

/* ------------------------------------------------------------------ */
/* 数据库连接 /api/v1/db-connection/*（数据库节点面板）                      */
/* ------------------------------------------------------------------ */

export type DbConnectionListItem = {
  id: string
  name: string
  db_type: string
  host: string
  port: number
  database: string
  username: string
  description?: string
  connect_params?: Record<string, unknown>
  user_id?: string
  enterprise_id?: string
  create_time?: string
  update_time?: string
  table_count: number
}

export type DbConnectionDetail = DbConnectionListItem & {
  password?: string
  tables?: DatabaseTableRef[]
}

export type DbConnectionListResult = {
  list: DbConnectionListItem[]
  total: number
}

export type DbConnectionUpdate = {
  id: string
  name?: string
  host?: string
  port?: number
  database?: string
  username?: string
  password?: string
  description?: string
  connect_params?: Record<string, unknown>
}

export type DbConnectionCreate = {
  name: string
  db_type: string
  host: string
  port: number
  database: string
  username: string
  password: string
  description?: string
  connect_params?: Record<string, unknown>
}

function normalizeListPayload(raw: any): DbConnectionListResult {
  if (!raw) return { list: [], total: 0 }
  const list = raw.list ?? raw.items ?? raw.records ?? (Array.isArray(raw) ? raw : [])
  const total = Number(raw.total ?? raw.count ?? list.length) || list.length
  return { list, total }
}

/** GET /api/v1/db-connection/list */
export async function dbConnectionListApi(page = 1, limit = 100): Promise<DbConnectionListResult> {
  const safePage = Math.max(1, page)
  const safeLimit = Math.min(1000, Math.max(1, limit))
  const raw = await axios.get(`/api/v1/db-connection/list`, { params: { page: safePage, limit: safeLimit } })
  return normalizeListPayload(raw)
}

/** GET /api/v1/db-connection/detail?id=（编辑预填） */
export async function dbConnectionDetailApi(id: string): Promise<DbConnectionDetail> {
  const raw = await axios.get(`/api/v1/db-connection/detail`, { params: { id } })
  return raw as any
}

/** GET /api/v1/db-connection/tables?id=（弹窗展开时获取完整表 schema；失败返回 []；被 abort 时 re-throw） */
export async function dbConnectionTablesApi(id: string, signal?: AbortSignal): Promise<DatabaseTableRef[]> {
  try {
    const raw: any = await axios.get(`/api/v1/db-connection/tables`, {
      params: { id },
      signal,
      headers: {
        'X-Skip-Global-Error': 'true' // 跳过全局错误处理，取消请求时不触发全局报错弹窗
      }
    })
    if (raw.tables && raw.tables.length > 0) {
      return raw.tables
    }
    return []
  } catch (err) {
    // 请求被主动取消时 re-throw，让调用方的 .then 不执行（.finally 仍会执行以清理 loading 状态）
    // axios 取消请求会抛出 CanceledError（name === 'CanceledError' 或 code === 'ERR_CANCELED'）
    if ((err as any)?.name === 'CanceledError' || (err as any)?.code === 'ERR_CANCELED') throw err
    // 非取消错误（如 451 内容不合规等业务错误），补上提示
    const errMsg = typeof err === 'string' ? err : (err as any)?.message || '获取表结构失败'
    showMessage({ content: errMsg, type: 'error' })
    return []
  }
}

/** POST /api/v1/db-connection/create */
export async function dbConnectionCreateApi(body: DbConnectionCreate): Promise<{ id: string }> {
  const raw: any = await axios.post(`/api/v1/db-connection/create`, body)
  const id = raw?.data?.id ?? raw?.id ?? raw?.data?.connection_id ?? ''
  return { id }
}

/** POST /api/v1/db-connection/test */
export async function dbConnectionTestApi(body: Partial<DbConnectionCreate> & { id?: string }): Promise<void> {
  await axios.post(`/api/v1/db-connection/test`, body)
}

/** POST /api/v1/db-connection/update */
export async function dbConnectionUpdateApi(body: DbConnectionUpdate): Promise<void> {
  await axios.post(`/api/v1/db-connection/update`, body)
}

/** POST /api/v1/db-connection/delete */
export async function dbConnectionDeleteApi(id: string): Promise<void> {
  await axios.post(`/api/v1/db-connection/delete`, { id })
}

/* 工作流 */

/**
 * 列表
 */
export async function workflowListApi(page = 1, pageSize = 20, name = '', category = '') {
  try {
    const response: { data: any[]; total: number } = await axios.get(
      `/api/v1/workflow/list?page=${page}&limit=${pageSize}&name=${name}&category_code=${category}`
    )
    return response
  } catch (error) {
    console.error(error)
    throw error
  }
}

export async function workflowPublishListApi(page = 1, pageSize = 20, name = '', category = '') {
  try {
    const response: { data: any[]; total: number } = await axios.get(
      `/api/v1/workflow/list?status=${1}&page=${page}&limit=${pageSize}&name=${name}&category_code=${category}`
    )
    return response
  } catch (error) {
    console.error(error)
    throw error
  }
}
/**
 * 新增
 */
export const workflowCreateApi = async data => {
  return await axios.post(`/api/v1/workflow/create`, data)
}

/**
 * 编辑
 */
export const workflowEditApi = async (flow_id, data) => {
  return await axios.post(`/api/v1/workflow/update/${flow_id}`, data)
}

/* 
获取工作流在智能体中被引用数
*/
export async function flowUsedCountByAstApi(id) {
  return await axios.get(`/api/v1/assistant/used_flow_count?id=${id}`)
}

/* 
获取知识库在工作流中被引用数
*/
export async function knowledgeUsedCountByFlowApi(id) {
  return await axios.get(`/api/v1/workflow/used_knowledge_count?id=${id}`)
}

/* 
获取插件在工作流中被引用数
*/
export async function toolUsedCountByFlowApi(id) {
  return await axios.get(`/api/v1/workflow/used_tool_count?id=${id}`)
}

/* 删除 */
export const workflowDelApi = async flow_id => {
  return await axios.post(`/api/v1/workflow/delete/${flow_id}`, {})
}

/* 详情 */
export async function getWorkFlowDetailApi(flowId: string): Promise<any> {
  return axios.get(`/api/v1/workflow/detail/${flowId}`)
}

/* 发布 */
export const workflowPublishApi = async flow_id => {
  return await axios.post(`/api/v1/workflow/publish/${flow_id}`, {})
}

/* 取消发布 */
export const workflowUnpublishApi = async flow_id => {
  return await axios.post(`/api/v1/workflow/unpublish/${flow_id}`, {})
}

/**
 * 通过 URL 批量解析文件信息
 * 用于试运行抽屉填写 URL 时，获取文件名、大小等元信息
 * @param urls 文件 URL 数组
 */
export const batchUploadByUrls = async (
  urls: string[]
): Promise<{
  total: number
  success_count: number
  failed_count: number
  files: Array<{
    success: boolean
    file_name: string
    file_url: string | null
    file_id: string | null
    size: number | null
    file_type: string | null
    format: string | null
    error: string | null
  }>
}> => {
  return await axios.post('/api/v1/common/upload/batch/validate-url', { urls })
}

/* 单节点运行 */
export const singleNodeRun = async (data, options = {}) => {
  return await axios.post(`/api/v1/workflow/node_run`, data, options)
}
